'use client';

// ── Shared Lightbox component ─────────────────────────────────────────────────
// Cycle 55: extracted from Path.tsx (cycle 54) into a reusable component.
// Consumers: Path.tsx (city photos), Family.tsx (family photos).
//
// Features:
//   - FLIP magic-move animation: panel grows from thumbnail rect on open,
//     shrinks back on close. Driven by JS inline transforms (useLayoutEffect).
//   - ease-out-quint open  (0.22, 1, 0.36, 1) — 500ms
//   - ease-in-quart close  (0.5, 0, 0.75, 0)  — 400ms
//   - prefers-reduced-motion: FLIP skipped; backdrop animation suppressed.
//   - Accessible: focus trap on close button, ESC close, click-outside close,
//     focus returned to trigger on close (caller's responsibility via onClose).
//   - Panel aspect-ratio: matches photo's intrinsic w/h so the panel hugs the
//     image — no letterboxing for landscape or portrait images.
//
// Usage:
//   import { Lightbox, LightboxPhoto } from '@/components/photos/Lightbox';
//
//   const [photo, setPhoto] = useState<LightboxPhoto | null>(null);
//   const [sourceRect, setSourceRect] = useState<DOMRect | null>(null);
//
//   // On thumbnail click:
//   setSourceRect(btn.getBoundingClientRect());
//   setPhoto({ src, alt, cap, w, h });
//
//   // In JSX:
//   {photo && (
//     <Lightbox photo={photo} sourceRect={sourceRect} onClose={() => { setPhoto(null); setSourceRect(null); btn.focus(); }} />
//   )}

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Reduced-motion detection — used to skip FLIP animation when user requests it
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface LightboxPhoto {
  src: string;
  alt: string;
  cap?: string;
  /** Intrinsic image width in pixels — used to set panel aspect-ratio so the
   *  panel hugs the image's native shape. No letterboxing for any aspect ratio. */
  w: number;
  /** Intrinsic image height in pixels */
  h: number;
}

export function Lightbox({
  photo,
  onClose,
  sourceRect,
}: {
  photo: LightboxPhoto;
  onClose: () => void;
  sourceRect: DOMRect | null;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // closing: true triggers exit animation classes on backdrop; onClose fires after 400ms delay
  const [closing, setClosing] = useState(false);

  // FLIP open animation — runs synchronously before first paint via useLayoutEffect.
  // Uses ease-out-quint (motion-design.md) for dramatic, smooth growth from thumbnail.
  // 60ms opacity delay: panel is already moving before it becomes visible, so user
  // perceives motion-from-thumbnail rather than a separate fade-in.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !sourceRect || prefersReducedMotion()) return;

    const finalRect = panel.getBoundingClientRect();
    const dx = (sourceRect.left + sourceRect.width / 2) - (finalRect.left + finalRect.width / 2);
    const dy = (sourceRect.top + sourceRect.height / 2) - (finalRect.top + finalRect.height / 2);
    const sx = sourceRect.width / finalRect.width;
    const sy = sourceRect.height / finalRect.height;

    // Step 1: snap panel to source position+size with no transition
    panel.style.transformOrigin = 'center center';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';
    panel.style.transition = 'none';

    // Force reflow so the browser registers the initial transform before animating
    void panel.offsetHeight;

    // Step 2: animate to final centered identity.
    // ease-out-quint: cubic-bezier(0.22, 1, 0.36, 1) — snappy, confident growth with no overshoot.
    panel.style.transition =
      'transform 500ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out 60ms';
    panel.style.transform = 'translate(0, 0) scale(1)';
    panel.style.opacity = '1';
  }, [sourceRect]);

  // FLIP close animation — shrinks panel back to source thumbnail position.
  // ease-in-quart: slow start, fast finish — panel rushes into thumbnail then fades.
  // 120ms opacity delay keeps panel visible during shrink; fades last.
  const handleClose = useCallback(() => {
    const panel = panelRef.current;
    if (!panel || !sourceRect || prefersReducedMotion()) {
      onClose();
      return;
    }

    const finalRect = panel.getBoundingClientRect();
    const dx = (sourceRect.left + sourceRect.width / 2) - (finalRect.left + finalRect.width / 2);
    const dy = (sourceRect.top + sourceRect.height / 2) - (finalRect.top + finalRect.height / 2);
    const sx = sourceRect.width / finalRect.width;
    const sy = sourceRect.height / finalRect.height;

    // ease-in-quart: cubic-bezier(0.5, 0, 0.75, 0) — accelerates hard into thumbnail ("sucked in")
    panel.style.transition =
      'transform 400ms cubic-bezier(0.5, 0, 0.75, 0), opacity 280ms ease-in 120ms';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';

    // Backdrop fades via CSS class simultaneously
    setClosing(true);

    const id = setTimeout(() => onClose(), 400);
    return () => clearTimeout(id);
  }, [onClose, sourceRect]);

  // Focus the close button on mount
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // ESC key — delegates to handleClose for animated exit
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Click outside the image panel — delegates to handleClose
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) handleClose();
  };

  return (
    <>
      <div
        ref={backdropRef}
        className={`city-lightbox-backdrop${closing ? ' city-lightbox-backdrop--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Photo: ${photo.alt}`}
        onClick={handleBackdropClick}
      >
        {/* Panel: aspect-ratio set inline from photo.w/photo.h so panel hugs the
            image's native shape. max-width + max-height constrain to viewport.
            width: auto lets the panel shrink below 720px on narrow screens. */}
        <div
          ref={panelRef}
          className={`city-lightbox-panel${closing ? ' city-lightbox-panel--closing' : ''}`}
          style={{ aspectRatio: `${photo.w} / ${photo.h}` }}
        >
          <button
            ref={closeRef}
            className="city-lightbox-close"
            aria-label="Close photo"
            onClick={handleClose}
          >
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <img
            src={photo.src}
            alt={photo.alt}
            className="city-lightbox-img"
          />
          {photo.cap && (
            <p className="city-lightbox-caption">{photo.cap}</p>
          )}
        </div>
      </div>

      {/* Co-located styles — component is self-contained; consumers import nothing extra */}
      <style>{`
        /* ── Lightbox ── */
        /* FLIP / shared-element animation (cycle 54, extracted cycle 55):
           Panel open/close driven by JS inline transforms (useLayoutEffect).
           Backdrop fades independently via CSS keyframes.
           motion-design.md: ease-out-quint open (0.22,1,0.36,1); ease-in-quart close (0.5,0,0.75,0).
           No bounce/overshoot — motion-design.md explicitly warns against elastic curves. */
        .city-lightbox-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(14, 10, 24, 0.90);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--space-24);
          animation: lightbox-fade-in 400ms cubic-bezier(0.25, 1, 0.5, 1) both;
        }

        /* Exit: closing class swaps in reverse animation — matches panel's 400ms close */
        .city-lightbox-backdrop--closing {
          animation: lightbox-fade-out 400ms cubic-bezier(0.4, 0, 1, 1) both;
        }

        /* Panel: width + max constraints let the browser solve for the correct
           dimension from aspect-ratio + viewport limits.
           width: auto — shrinks on narrow viewports.
           max-width: min(90vw, 1600px) — fills most of the viewport on desktop;
             1600px cap prevents the panel exceeding the image's native resolution.
           max-height: 90vh — caps height on short viewports (portrait images especially).
           Mobile (<768px): max-width: 95vw; max-height: 88vh — uses available width.
           aspect-ratio set inline from photo.w/photo.h. */
        .city-lightbox-panel {
          position: relative;
          width: auto;
          max-width: min(90vw, 1600px);
          max-height: 90vh;
          border-radius: var(--radius-lg);
          overflow: hidden;
          background: var(--bg-2);
          box-shadow: 0 0 0 2px var(--gold), 0 32px 80px rgba(0,0,0,0.6);
          will-change: transform, opacity;
        }
        @media (max-width: 767px) {
          .city-lightbox-panel {
            max-width: 95vw;
            max-height: 88vh;
          }
        }

        /* Image fills panel exactly — with panel aspect-ratio matching the image,
           cover and contain produce identical rendering. Cover chosen as safer
           against sub-pixel dimension drift. */
        .city-lightbox-img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .city-lightbox-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          font-family: var(--font-sans);
          font-size: var(--text-photo-caption);
          color: var(--text-muted);
          text-align: center;
          padding: var(--space-14) var(--space-20) var(--space-16);
          margin: 0;
          background: linear-gradient(to top, rgba(14,10,24,0.80) 0%, transparent 100%);
        }

        .city-lightbox-close {
          position: absolute;
          top: var(--space-12);
          right: var(--space-12);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--bg-scrim-play);
          border: 1px solid var(--border-gold-hover);
          color: var(--text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 1;
          transition: background 200ms ease, border-color 200ms ease;
        }
        .city-lightbox-close:hover {
          background: var(--bg);
          border-color: var(--gold-dim);
        }
        .city-lightbox-close:focus-visible {
          outline: 2px solid var(--gold);
          outline-offset: 2px;
        }

        /* Backdrop fade keyframes — panel uses JS-driven FLIP, not keyframes */
        @keyframes lightbox-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lightbox-fade-out {
          from { opacity: 1; }
          to   { opacity: 0; }
        }

        /* Reduced motion: suppress backdrop animation; JS FLIP skips via prefersReducedMotion() */
        @media (prefers-reduced-motion: reduce) {
          .city-lightbox-backdrop,
          .city-lightbox-backdrop--closing {
            animation: none;
          }
          .city-lightbox-panel {
            will-change: auto;
          }
        }
      `}</style>
    </>
  );
}
